/**
 * Web Summary Component
 *
 * Displays web/marketing metrics and traffic source analytics
 */

import { AlertCircle, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useFirebaseAnalytics } from "../hooks/use-firebase-analytics";
import type { WebSummary as WebSummaryType } from "../types/web-types";
import { LockedChartCard } from "./locked-chart-card";
import { LockedMetricCard } from "./locked-metric-card";
import { MetricCard } from "./metric-card";
import { TrafficSourceChart } from "./traffic-source-chart";
import { TrafficSourceTable } from "./traffic-source-table";

interface WebSummaryProps {
  data: WebSummaryType | null;
}

export function WebSummary({ data }: WebSummaryProps) {
  const { t } = useTranslation("reports");
  const {
    data: firebaseData,
    loading: firebaseLoading,
    error: firebaseError,
    refetch: refetchFirebase,
  } = useFirebaseAnalytics();
  const hasStaleData = Boolean(firebaseError && firebaseData?.engagement);

  // If data is null (basic tier), show locked version with mock data
  if (data === null) {
    const mockTrafficData = [
      { name: "Google", bookings: 45, revenue: 3500 },
      { name: "Instagram", bookings: 32, revenue: 2800 },
      { name: "Direct", bookings: 28, revenue: 2200 },
      { name: "Facebook", bookings: 15, revenue: 1200 },
    ];

    const mockChartContent = (
      <div className="space-y-6">
        {/* Mock Bookings Chart */}
        <div>
          <h4 className="text-xs font-medium mb-2">{t("common.bookings")}</h4>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={mockTrafficData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis dataKey="name" className="text-xs" tick={{ fontSize: 10 }} tickLine={false} />
              <YAxis className="text-xs" tick={{ fontSize: 10 }} tickLine={false} />
              <Bar dataKey="bookings" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        {/* Mock Revenue Chart */}
        <div>
          <h4 className="text-xs font-medium mb-2">{t("common.revenue")}</h4>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={mockTrafficData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis dataKey="name" className="text-xs" tick={{ fontSize: 10 }} tickLine={false} />
              <YAxis className="text-xs" tick={{ fontSize: 10 }} tickLine={false} />
              <Bar dataKey="revenue" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );

    const mockTableContent = (
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b">
              <th className="text-left p-2 font-medium">{t("common.source")}</th>
              <th className="text-right p-2 font-medium">{t("common.inquiries")}</th>
              <th className="text-right p-2 font-medium">{t("common.bookings")}</th>
              <th className="text-right p-2 font-medium">{t("common.conversionRate")}</th>
              <th className="text-right p-2 font-medium">{t("common.revenue")}</th>
            </tr>
          </thead>
          <tbody>
            {mockTrafficData.map((row, idx) => (
              <tr key={idx} className="border-b">
                <td className="p-2 font-medium">{row.name}</td>
                <td className="p-2 text-right">000</td>
                <td className="p-2 text-right">{row.bookings}</td>
                <td className="p-2 text-right">00.0%</td>
                <td className="p-2 text-right">€0,000</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );

    return (
      <div className="space-y-4">
        {/* Locked Metric Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <LockedMetricCard
            label={t("metrics.totalInquiries.label")}
            tooltip={t("metrics.totalInquiries.tooltip")}
          />
          <LockedMetricCard
            label={t("metrics.totalWebBookings.label")}
            tooltip={t("metrics.totalWebBookings.tooltip")}
          />
          <LockedMetricCard
            label={t("metrics.conversionRate.label")}
            tooltip={t("metrics.conversionRate.tooltip")}
          />
        </div>

        {/* Locked Traffic Source Chart */}
        <LockedChartCard
          title={t("charts.trafficSources.title")}
          tooltip={t("charts.trafficSources.tooltip")}
          mockContent={mockChartContent}
          height="auto"
        />

        {/* Locked Traffic Source Table */}
        <LockedChartCard
          title={t("charts.trafficSources.title")}
          tooltip={t("charts.trafficSources.tooltip")}
          mockContent={mockTableContent}
          height="auto"
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Firebase Analytics Metrics - Individual Cards */}
      {firebaseData?.engagement && firebaseData.comparisonEngagement && (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <MetricCard
            metric={{
              type: "absolute",
              label: t("metrics.activeUsers.label"),
              value: {
                current: firebaseData.engagement.activeUsers,
                previous: firebaseData.comparisonEngagement.activeUsers,
                change:
                  firebaseData.engagement.activeUsers -
                  firebaseData.comparisonEngagement.activeUsers,
                changePercent:
                  firebaseData.comparisonEngagement.activeUsers > 0
                    ? ((firebaseData.engagement.activeUsers -
                        firebaseData.comparisonEngagement.activeUsers) /
                        firebaseData.comparisonEngagement.activeUsers) *
                      100
                    : 0,
                isIncrease:
                  firebaseData.engagement.activeUsers >
                  firebaseData.comparisonEngagement.activeUsers,
                isPositive:
                  firebaseData.engagement.activeUsers >=
                  firebaseData.comparisonEngagement.activeUsers,
              },
              format: undefined,
            }}
            tooltip={t("metrics.activeUsers.tooltip")}
          />

          <MetricCard
            metric={{
              type: "absolute",
              label: t("metrics.sessions.label"),
              value: {
                current: firebaseData.engagement.sessions,
                previous: firebaseData.comparisonEngagement.sessions,
                change:
                  firebaseData.engagement.sessions - firebaseData.comparisonEngagement.sessions,
                changePercent:
                  firebaseData.comparisonEngagement.sessions > 0
                    ? ((firebaseData.engagement.sessions -
                        firebaseData.comparisonEngagement.sessions) /
                        firebaseData.comparisonEngagement.sessions) *
                      100
                    : 0,
                isIncrease:
                  firebaseData.engagement.sessions > firebaseData.comparisonEngagement.sessions,
                isPositive:
                  firebaseData.engagement.sessions >= firebaseData.comparisonEngagement.sessions,
              },
              format: undefined,
            }}
            tooltip={t("metrics.sessions.tooltip")}
          />

          <MetricCard
            metric={{
              type: "absolute",
              label: t("metrics.pageViews.label"),
              value: {
                current: firebaseData.engagement.pageViews,
                previous: firebaseData.comparisonEngagement.pageViews,
                change:
                  firebaseData.engagement.pageViews - firebaseData.comparisonEngagement.pageViews,
                changePercent:
                  firebaseData.comparisonEngagement.pageViews > 0
                    ? ((firebaseData.engagement.pageViews -
                        firebaseData.comparisonEngagement.pageViews) /
                        firebaseData.comparisonEngagement.pageViews) *
                      100
                    : 0,
                isIncrease:
                  firebaseData.engagement.pageViews > firebaseData.comparisonEngagement.pageViews,
                isPositive:
                  firebaseData.engagement.pageViews >= firebaseData.comparisonEngagement.pageViews,
              },
              format: undefined,
            }}
            tooltip={t("metrics.pageViews.tooltip")}
          />

          <MetricCard
            metric={{
              type: "absolute",
              label: t("metrics.avgSessionDuration.label"),
              value: {
                current: firebaseData.engagement.avgSessionDuration,
                previous: firebaseData.comparisonEngagement.avgSessionDuration,
                change:
                  firebaseData.engagement.avgSessionDuration -
                  firebaseData.comparisonEngagement.avgSessionDuration,
                changePercent:
                  firebaseData.comparisonEngagement.avgSessionDuration > 0
                    ? ((firebaseData.engagement.avgSessionDuration -
                        firebaseData.comparisonEngagement.avgSessionDuration) /
                        firebaseData.comparisonEngagement.avgSessionDuration) *
                      100
                    : 0,
                isIncrease:
                  firebaseData.engagement.avgSessionDuration >
                  firebaseData.comparisonEngagement.avgSessionDuration,
                isPositive:
                  firebaseData.engagement.avgSessionDuration >=
                  firebaseData.comparisonEngagement.avgSessionDuration,
              },
              format: "duration",
            }}
            tooltip={t("metrics.avgSessionDuration.tooltip")}
          />

          <MetricCard
            metric={{
              type: "absolute",
              label: t("metrics.bounceRate.label"),
              value: {
                current: firebaseData.engagement.bounceRate,
                previous: firebaseData.comparisonEngagement.bounceRate,
                change:
                  firebaseData.engagement.bounceRate - firebaseData.comparisonEngagement.bounceRate,
                changePercent:
                  firebaseData.comparisonEngagement.bounceRate > 0
                    ? ((firebaseData.engagement.bounceRate -
                        firebaseData.comparisonEngagement.bounceRate) /
                        firebaseData.comparisonEngagement.bounceRate) *
                      100
                    : 0,
                isIncrease:
                  firebaseData.engagement.bounceRate > firebaseData.comparisonEngagement.bounceRate,
                isPositive:
                  firebaseData.engagement.bounceRate <=
                  firebaseData.comparisonEngagement.bounceRate,
              },
              format: "percentage",
            }}
            tooltip={t("metrics.bounceRate.tooltip")}
          />
        </div>
      )}

      {/* Firebase Analytics Loading State */}
      {firebaseLoading && (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="pb-1">
                <div className="h-4 bg-muted rounded w-24" />
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="h-8 bg-muted rounded w-16" />
                <div className="h-3 bg-muted rounded w-32" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Firebase Analytics Error State */}
      {firebaseError && (
        <Alert variant={hasStaleData ? "default" : "destructive"}>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between gap-3">
            <span>
              {hasStaleData ? t("firebaseAlerts.outdated") : t("firebaseAlerts.notConfigured")}
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => refetchFirebase()}
              disabled={firebaseLoading}
            >
              <RefreshCw className={`mr-2 h-3.5 w-3.5 ${firebaseLoading ? "animate-spin" : ""}`} />
              {t("firebaseAlerts.refresh")}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Primary Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MetricCard
          metric={{ ...data.totalInquiries, label: t("metrics.totalInquiries.label") }}
          tooltip={t("metrics.totalInquiries.tooltip")}
        />
        <MetricCard
          metric={{ ...data.totalBookings, label: t("metrics.totalWebBookings.label") }}
          tooltip={t("metrics.totalWebBookings.tooltip")}
        />
        <MetricCard
          metric={{ ...data.overallConversionRate, label: t("metrics.conversionRate.label") }}
          tooltip={t("metrics.conversionRate.tooltip")}
        />
      </div>

      {/* Traffic Source Chart */}
      <TrafficSourceChart
        current={data.trafficSources.current}
        previous={data.trafficSources.previous}
      />

      {/* Traffic Source Table */}
      <TrafficSourceTable
        current={data.trafficSources.current}
        previous={data.trafficSources.previous}
      />
    </div>
  );
}
